import React, {useState, useEffect, useCallback} from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Box,
  Autocomplete,
  Chip,
  Checkbox,
  CircularProgress,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import axios from 'axios'
import { toast } from 'react-toastify'

interface BehaviorOption {
  value: string
  label: string
}

interface BehaviorRecord {
  targetBehavior: string
  targetId: string
  replacementBehaviors: string[]
  replacementIds: string[]
}

interface BehaviorMappingTableProps {
  onBehaviorDataChange?: (behaviorData: {
    behaviorsMapping: Array<{
      targetId: string
      replacementIds: string[]
    }>
  }) => void
  canAddNew?: boolean
  showDeleteButton?: boolean
  initialTargetBehaviour?: (string | null)[]
  initialReplacementBehaviour?: string[][]
  initialTargetIds?: string[]
  initialReplacementIds?: string[][]
}

const BehaviorMappingTable: React.FC<BehaviorMappingTableProps> = ({
  onBehaviorDataChange,
  canAddNew = true,
  showDeleteButton = true,
  initialTargetBehaviour = [],
  initialReplacementBehaviour = [],
  initialTargetIds = [],
  initialReplacementIds = [],
}) => {
  const [targets, setTargets] = useState<BehaviorOption[]>([])
  const [replacements, setReplacements] = useState<BehaviorOption[]>([])
  const [targetBehaviour, setTargetBehaviour] = useState<(string | null)[]>(() => {
    return initialTargetBehaviour &&
      initialTargetBehaviour.length > 0 &&
      initialTargetBehaviour[0] !== null
      ? initialTargetBehaviour
      : []
  })
  const [targetIds, setTargetIds] = useState<string[]>(() => {
    return initialTargetIds && initialTargetIds.length > 0 ? initialTargetIds : []
  })
  const [replacementBehaviour, setReplacementBehaviour] = useState<string[][]>(() => {
    console.log('Initial replacementBehaviour:', initialReplacementBehaviour)
    return initialReplacementBehaviour && initialReplacementBehaviour.length > 0
      ? initialReplacementBehaviour
      : []
  })
  const [replacementIds, setReplacementIds] = useState<string[][]>(() => {
    return initialReplacementIds && initialReplacementIds.length > 0 ? initialReplacementIds : []
  })

  // Update state when initial data changes (for edit mode)
  useEffect(() => {
    if (initialTargetBehaviour && initialTargetBehaviour.length > 0) {
      setTargetBehaviour(initialTargetBehaviour)
    }
    if (initialTargetIds && initialTargetIds.length > 0) {
      setTargetIds(initialTargetIds)
    }
    if (initialReplacementBehaviour && initialReplacementBehaviour.length > 0) {
      setReplacementBehaviour(initialReplacementBehaviour)
    }
    if (initialReplacementIds && initialReplacementIds.length > 0) {
      setReplacementIds(initialReplacementIds)
    }
  }, [initialTargetBehaviour, initialTargetIds, initialReplacementBehaviour, initialReplacementIds])

  const [dropDownLoader, setDropDownLoader] = useState(false)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [newBehavior, setNewBehavior] = useState<BehaviorRecord>({
    targetBehavior: '',
    targetId: '',
    replacementBehaviors: [],
    replacementIds: [],
  })

  const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
  const checkedIcon = <CheckBoxIcon fontSize='small' />

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setDropDownLoader(true)
      setLoading(true)
      try {
        const [replacementResponse, targetResponse] = await Promise.all([
          axios.get(`/options?type=REPLACEMENT_BEHAVIOR`),
          axios.get(`/options?type=TARGET_BEHAVIOR`),
        ])
        const {data: replacementData} = replacementResponse.data
        const {data: targetData} = targetResponse.data

        const formattedReplacements = replacementData.map((item: any) => ({
          value: item?._id,
          label: item.name,
        }))
        const formattedTargets = targetData.map((item: any) => ({
          value: item?._id,
          label: item.name,
        }))

        setReplacements(formattedReplacements)
        setTargets(formattedTargets)
      } catch (error) {
        console.error('Error fetching behavior data:', error)
      } finally {
        setDropDownLoader(false)
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Memoize the behavior data to prevent unnecessary re-renders
  const behaviorData = React.useMemo(
    () => ({
      behaviorsMapping: targetIds.map((targetId, index) => ({
        targetId: targetId,
        replacementIds: replacementIds[index]?.map(replacementId => replacementId) || []
      }))
    }),
    [targetIds, replacementIds]
  )

  // Notify parent component when data changes - with proper dependency management
  useEffect(() => {
    if (onBehaviorDataChange) {
      onBehaviorDataChange(behaviorData)
    }
  }, [behaviorData, onBehaviorDataChange])

  const handleAddBehavior = useCallback(() => {
    if (newBehavior.targetBehavior && newBehavior.replacementBehaviors.length > 0) {
      // Create new arrays with the new behavior
      const newTargetBehaviour = [...targetBehaviour, newBehavior.targetBehavior]
      const newTargetIds = [...targetIds, newBehavior.targetId]
      const newReplacementBehaviour = [...replacementBehaviour, newBehavior.replacementBehaviors]
      const newReplacementIds = [...replacementIds, newBehavior.replacementIds]

      setTargetBehaviour(newTargetBehaviour)
      setTargetIds(newTargetIds)
      setReplacementBehaviour(newReplacementBehaviour)
      setReplacementIds(newReplacementIds)

      setNewBehavior({
        targetBehavior: '',
        targetId: '',
        replacementBehaviors: [],
        replacementIds: [],
      })
      setOpen(false)
    } else {
        toast.error('Please select a target behavior and replacement behaviors')
    }
  }, [newBehavior, targetBehaviour, targetIds, replacementBehaviour, replacementIds])

  const handleDeleteBehavior = useCallback(
    (index: number) => {
      const newTargetBehaviour = [...targetBehaviour]
      const newTargetIds = [...targetIds]
      const newReplacementBehaviour = [...replacementBehaviour]
      const newReplacementIds = [...replacementIds]
      newTargetBehaviour.splice(index, 1)
      newTargetIds.splice(index, 1)
      newReplacementBehaviour.splice(index, 1)
      newReplacementIds.splice(index, 1)

      setTargetBehaviour(newTargetBehaviour)
      setTargetIds(newTargetIds)
      setReplacementBehaviour(newReplacementBehaviour)
      setReplacementIds(newReplacementIds)
    },
    [targetBehaviour, targetIds, replacementBehaviour, replacementIds]
  )

  const handleInputChange = useCallback((field: keyof BehaviorRecord, value: any) => {
    setNewBehavior((prev) => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
          setNewBehavior({
        targetBehavior: '',
        targetId: '',
        replacementBehaviors: [],
        replacementIds: [],
      })
  }, [])

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', padding: 2}}>
        <CircularProgress />
      </Box>
    )
  }
  
  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{backgroundColor: '#2f3347'}}>
            <TableRow>
              {showDeleteButton && <TableCell style={{color: 'white'}}>Actions</TableCell>}
              <TableCell style={{color: 'white'}}>Target Behavior</TableCell>
              <TableCell style={{color: 'white'}}>Replacement Behaviors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {targetBehaviour.length > 0 ? (
              targetBehaviour.map((target, index) => (
                <TableRow key={index}>
                  {showDeleteButton && (
                    <TableCell>
                      <IconButton onClick={() => handleDeleteBehavior(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell>{target || 'No Target Selected'}</TableCell>
                  <TableCell>
                    {replacementBehaviour[index]?.length > 0
                      ? replacementBehaviour[index].map((replacement, repIndex) => (
                          <div key={repIndex} style={{marginBottom: '4px'}}>
                            {replacement}
                            {repIndex < replacementBehaviour[index].length - 1 ? ',' : ''}
                          </div>
                        ))
                      : 'No replacements selected'}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={showDeleteButton ? 3 : 2}
                  align='center'
                  style={{fontWeight: 'bold', fontSize: '16px'}}
                >
                  No Behavior Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant='contained' onClick={handleOpen} startIcon={<AddIcon />} sx={{mt: 2, mb: 2}}>
        Add Behavior Record
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>Add New Behavior Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1}}>
            <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2}}>
              <FormControl fullWidth size='small'>
                <Autocomplete
                  id='target-autocomplete'
                  loading={dropDownLoader}
                  options={targets.filter(
                    (targetOption) => !targetBehaviour.includes(targetOption.label)
                  )}
                  getOptionLabel={(option) => option.label}
                  value={
                    targets.find((target) => target.label === newBehavior.targetBehavior) || null
                  }
                                      onChange={(event, newValue) => {
                      handleInputChange('targetBehavior', newValue ? newValue.label : '')
                      handleInputChange('targetId', newValue ? newValue.value : '')
                    }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Target Behavior'
                      variant='outlined'
                      size='small'
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {dropDownLoader ? <CircularProgress color='inherit' size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  size='small'
                />
              </FormControl>
              <FormControl fullWidth size='small'>
                <Autocomplete
                  multiple
                  id='replacement-autocomplete'
                  loading={dropDownLoader}
                  options={replacements}
                  disableCloseOnSelect
                  forcePopupIcon={true}
                  getOptionLabel={(option) => option.label}
                  value={replacements.filter((replacement) =>
                    newBehavior.replacementBehaviors.includes(replacement.label)
                  )}
                                      onChange={(event, newValue) => {
                      handleInputChange(
                        'replacementBehaviors',
                        newValue.map((option) => option.label)
                      )
                      handleInputChange(
                        'replacementIds',
                        newValue.map((option) => option.value)
                      )
                    }}
                  renderOption={(props, option, {selected}) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{marginRight: 8}}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Replacement Behaviors'
                      variant='outlined'
                      size='small'
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {dropDownLoader ? <CircularProgress color='inherit' size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value) =>
                    value.map((option, index) => (
                      <span key={index} style={{marginRight: '8px', marginBottom: '8px'}}>
                        {option.label}
                      </span>
                    ))
                  }
                  size='small'
                />
              </FormControl>
            </Grid>
              
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleAddBehavior}
            disabled={!newBehavior.targetBehavior || newBehavior.replacementBehaviors.length === 0}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BehaviorMappingTable
